import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalStorageService } from './local-storage.service';

/**
 * Centralized, cached access to the auth token.
 * - Reads the token from localStorage once and caches it.
 * - Keeps multiple tabs in sync via the 'storage' event.
 * - Exposes both an accessor (getToken) and an observable (token$).
 *
 * Store only the RAW token value here (e.g., "abc123"), NOT the "token:" prefix.
 * Interceptors/utilities are responsible for prefixing where necessary.
 */
@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private static readonly STORAGE_KEY = 'auth_key';

  private readonly _token$ = new BehaviorSubject<string | null>(null);
  private _initialized = false;

  constructor(private localStorage: LocalStorageService) {
    // Sync across tabs
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key === AuthTokenService.STORAGE_KEY) {
        this._token$.next(e.newValue);
      }
    });
  }

  /** Ensure the subject is initialized from storage exactly once. */
  private ensureInit(): void {
    if (this._initialized) return;
    this._initialized = true;
    this._token$.next(this.localStorage.get(AuthTokenService.STORAGE_KEY));
  }

  /** Current token (raw, without "token:" prefix), or null if absent. */
  getToken(): string | null {
    this.ensureInit();
    return this._token$.value;
  }

  /** Observable stream of token changes (raw). */
  token$(): Observable<string | null> {
    this.ensureInit();
    return this._token$.asObservable();
  }

  /** Set or clear the token (raw). Persists and notifies subscribers. */
  setToken(token: string | null): void {
    this.ensureInit();
    if (token && token.length) {
      this.localStorage.set(AuthTokenService.STORAGE_KEY, token);
      this._token$.next(token);
    } else {
      this.localStorage.remove(AuthTokenService.STORAGE_KEY);
      this._token$.next(null);
    }
  }

  /** Clears the token; same as setToken(null). */
  clearToken(): void {
    this.setToken(null);
  }
}
